import { ForwardedRef, ReactNode, useImperativeHandle, useMemo, useState } from "react";
import { DataTable, type Column } from "@racoon/ui";
import { Namespace, useT } from "@racoon/i18n";
import { useAuthSession } from "~/shared/hooks/auth.session.admin";
import { DateValue } from "~/shared/lib/analytics-utils";
import { useReportExport } from "~/shared/hooks/use-report-export";
import { useTableSettings } from "~/shared/hooks/useTableSettings";

export interface ReportTableProps {
  startDate: DateValue;
  endDate: DateValue;
  /** The active filters as one line, for the PDF and print header. */
  filterInfo?: string;
}

/** What the page header's Excel, PDF and print buttons call on the active report. */
export interface ReportTableHandle {
  handleDownloadExcel: () => Promise<void>;
  handleDownloadPdf: () => Promise<void>;
  handlePrint: () => void;
}

interface Props<T> extends ReportTableProps {
  exportRef: ForwardedRef<ReportTableHandle>;
  /** Key the user's column settings are saved under. */
  settingsKey: string;
  reportName: string;
  columns: Column<T>[];
  rows: T[];
  /** The rows the exports and print get, e.g. with a totals line; defaults to rows. */
  exportRows?: T[];
  rowKey: (row: T) => string;
  isLoading: boolean;
  error?: unknown;
  /** Cards the table's links open. */
  children?: ReactNode;
}

/** The table, column settings and exports every Отчёты tab shares. */
export function ReportTable<T>({
  exportRef,
  settingsKey,
  reportName,
  columns,
  rows,
  exportRows = rows,
  rowKey,
  isLoading,
  error,
  children,
  startDate,
  endDate,
  filterInfo,
}: Props<T>) {
  const t = useT();
  const { activeCompanyId } = useAuthSession();
  const { columnConfig, handleColumnConfigChange } = useTableSettings(
    settingsKey,
    activeCompanyId || undefined,
  );
  // DataTable sorts internally; the exports and print follow the on-screen order, extra rows
  // (a totals line) after it.
  const [sortedRows, setSortedRows] = useState<T[]>();
  const orderedExportRows = useMemo(() => {
    if (!sortedRows || sortedRows.length !== rows.length) return exportRows;
    const shown = new Set<T>(rows);
    return [...sortedRows, ...exportRows.filter((row) => !shown.has(row))];
  }, [sortedRows, rows, exportRows]);
  const { handleExportExcel, handleExportPdf, handlePrint } = useReportExport({
    data: orderedExportRows,
    columns,
    reportName,
    startDate,
    endDate,
  });

  useImperativeHandle(exportRef, () => ({
    handleDownloadExcel: handleExportExcel,
    handleDownloadPdf: () => handleExportPdf(filterInfo),
    handlePrint: () => handlePrint(filterInfo),
  }));

  if (error) {
    return (
      <div className="p-4 text-center text-red-500">
        {t(Namespace.Analytics, "table.error.loading")}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-1 min-h-0">
        <DataTable
          data={rows}
          columns={columns}
          loading={isLoading}
          rowKey={rowKey}
          className="!rounded-xl min-h-0"
          columnConfig={columnConfig}
          onColumnConfigChange={handleColumnConfigChange}
          onSortedRowsChange={setSortedRows}
          namespace={Namespace.Analytics}
        />
      </div>
      {children}
    </div>
  );
}
